/**
 * Shared building blocks for the per-step output renderers.
 *
 * The renderers all live inside StepCard's "done" state, so they inherit the
 * coral via theme.palette.primary — per-product accent deferred (spec D2).
 * These helpers keep the eight renderers visually cohesive — section headers,
 * chip clusters, and the trailing narrative — without each one re-deriving the
 * same MUI boilerplate.
 */

import type { ReactNode } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

/** A small uppercase section label, used to head each block within a renderer. */
export function SectionLabel({ children }: { children: ReactNode }) {
  const theme = useTheme();
  return (
    <Typography
      variant="overline"
      sx={{
        display: "block",
        fontWeight: 800,
        letterSpacing: "0.08em",
        color: alpha(theme.palette.primary.main, 0.75),
        lineHeight: 1.4,
      }}
    >
      {children}
    </Typography>
  );
}

/** Render a string[] as a cluster of chips. Renders nothing when empty. */
export function ChipCluster({
  items,
  color,
  variant = "soft",
}: {
  items?: string[] | null;
  color?: string;
  variant?: "soft" | "outline";
}) {
  const theme = useTheme();
  const resolvedColor = color ?? theme.palette.primary.main;
  const list = items ?? [];
  if (list.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
      {list.map((item, i) => (
        <Chip
          key={`${item}-${i}`}
          label={item}
          size="small"
          sx={
            variant === "outline"
              ? {
                  fontWeight: 500,
                  borderColor: alpha(resolvedColor, 0.4),
                  color: resolvedColor,
                }
              : {
                  fontWeight: 500,
                  bgcolor: alpha(resolvedColor, 0.1),
                  color: resolvedColor,
                }
          }
          variant={variant === "outline" ? "outlined" : "filled"}
        />
      ))}
    </Stack>
  );
}

/** A labelled chip cluster block; omits itself entirely when there are no items. */
export function ChipSection({
  label,
  items,
  color,
  variant,
}: {
  label: string;
  items?: string[] | null;
  color?: string;
  variant?: "soft" | "outline";
}) {
  if ((items ?? []).length === 0) return null;
  return (
    <Box>
      <SectionLabel>{label}</SectionLabel>
      <Box mt={0.75}>
        <ChipCluster items={items} color={color} variant={variant} />
      </Box>
    </Box>
  );
}

/** A bulleted list of strings; omits itself when empty. */
export function BulletSection({
  label,
  items,
  color,
}: {
  label?: string;
  items?: string[] | null;
  color?: string;
}) {
  const theme = useTheme();
  // Dot is a decorative fill — use coralFill unless a specific color is passed in.
  const dotColor = color ?? theme.palette.primary.light;
  const list = items ?? [];
  if (list.length === 0) return null;
  return (
    <Box>
      {label && <SectionLabel>{label}</SectionLabel>}
      <Stack component="ul" spacing={0.5} sx={{ m: 0, mt: label ? 0.75 : 0, pl: 0, listStyle: "none" }}>
        {list.map((item, i) => (
          <Stack
            key={`${item}-${i}`}
            component="li"
            direction="row"
            spacing={1}
            alignItems="flex-start"
          >
            <Box
              component="span"
              sx={{
                mt: "7px",
                width: 6,
                height: 6,
                flexShrink: 0,
                borderRadius: "50%",
                bgcolor: dotColor,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {item}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

/** A horizontal score bar (0–100) with a label and trailing value. */
export function ScoreBar({
  label,
  value,
  max = 100,
  color,
}: {
  label: string;
  value: number;
  max?: number;
  color?: string;
}) {
  const theme = useTheme();
  // Text value label → primary.main (AA-safe). Bar fill → coralFill (decorative).
  const textColor = color ?? theme.palette.primary.main;
  const fillColor = color ?? theme.palette.primary.light;
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={0.5}>
        <Typography variant="body2" fontWeight={500} color="text.secondary">
          {label}
        </Typography>
        <Typography variant="caption" fontWeight={500} sx={{ color: textColor }}>
          {value}
        </Typography>
      </Stack>
      <Box sx={{ height: 8, borderRadius: 999, bgcolor: alpha(fillColor, 0.12), overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${pct}%`, borderRadius: 999, bgcolor: fillColor }} />
      </Box>
    </Box>
  );
}
